import { configureStore } from '@reduxjs/toolkit';
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import appReducer from 'features/app/appSlice';
import userReducer from 'features/auth/userSlice';
import betSlipReducer from 'features/betSlip/betSlipSlice';
import shopReducer from 'features/auth/shopSlice';
import kenoReducer from 'features/keno/kenoSlice';
import spinReducer from 'features/spin/spinSlice';
import gameReducer from 'features/games/gameSlice';
import cashierReducer from 'features/modals/cashierSlice';
import printReducer from 'features/app/printSlice';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { DEBUG } from 'constants/settings';
import { apiSlice } from './api/apiSlice';
import 'features/auth/api/authApiSlice';
import 'features/betSlip/api/betApiSlice';
import 'features/modals/api/cashierApiSlice';
import 'features/modals/api/cashierRetailApiSlice';
import 'features/sportsbook/api/sportsbookApiSlice';

const persistUserConfig = {
  key: 'user',
  storage,
};

/** Do not persist till float — it goes stale vs server and hid shop till vs legacy user field mismatch. */
const persistShopConfig = {
  key: 'shop',
  storage,
  version: 2,
  blacklist: ['tillBalance'],
  migrate: (state: Record<string, unknown> | undefined) => {
    if (state && typeof state === 'object' && 'tillBalance' in state) {
      const next = { ...state };
      delete next.tillBalance;
      return Promise.resolve(next);
    }
    return Promise.resolve(state);
  },
};

const persistPrintConfig = {
  key: 'print',
  storage,
  version: 2,
  /** v1 → v2: turn redeem auto-print on by default; cancel stays off unless already on. */
  migrate: (state: unknown) => {
    const prev =
      state && typeof state === 'object'
        ? (state as { cancel?: boolean; redeem?: boolean })
        : {};
    return Promise.resolve({
      cancel: Boolean(prev.cancel),
      redeem: true,
    });
  },
};

const persistedUserReducer = persistReducer(persistUserConfig, userReducer);
const persistedShopReducer = persistReducer(persistShopConfig, shopReducer);
const persistedPrintReducer = persistReducer(persistPrintConfig, printReducer);

export const store: ToolkitStore = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    user: persistedUserReducer,
    shop: persistedShopReducer,
    print: persistedPrintReducer,
    app: appReducer,
    keno: kenoReducer,
    spin: spinReducer,
    betSlip: betSlipReducer,
    game: gameReducer,
    cashier: cashierReducer,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({ serializableCheck: false }).concat(apiSlice.middleware);
  },
  devTools: DEBUG,
});

export const persistor = persistStore(store);
